import { useQuery } from "@apollo/client/react";
import type { ErrorLike, OperationVariables } from "@apollo/client";
import { AppData, MenuAction } from "@archetypes/app";
import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import { useIsMinimumTablet } from "@hooks/use-media-query";
import { GET_APP } from "@queries/index";
import { convertToPathObject, getLinkFromMenuParams, isValidSubMenu } from "@utils/app.util";
import { deleteKeysByPrefix } from "@utils/generic.util";
import { openExternalUrl } from "@utils/url.util";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  useEffect,
  useReducer,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

/**
 * 3) UI State
 * - expanded: Only tracks what the user manually expanded in this session.
 *   It resets on refresh (no persistence), as per your requirement.
 *
 * We intentionally do NOT store "active" or "has-active-child" here,
 * because those should be derived from the URL (menuId) and the menu index.
 */
export type MenuUIState = {
  expanded: Record<string, true>; // keys are submenu IDs
};

/**
 * 4) Actions
 * - TOGGLE_SUBMENU: user clicked a submenu title (expand/collapse it)
 * - COLLAPSE_ALL: optional utility (e.g. when navigating away or pressing a button)
 * - SET_EXPANDED: optional utility (if you ever want to programmatically set expanded state)
 */
export type UserMenuAction =
  | {
      type: "TOGGLE_SUBMENU";
      id: string;
      parentMenuPath: string;
      activeMenuTree: string[];
    }
  | { type: "COLLAPSE_ALL" };

export function menuReducer(state: MenuUIState, action: UserMenuAction): MenuUIState {
  switch (action.type) {
    case "TOGGLE_SUBMENU": {
      const isTopLevelMenu = action.parentMenuPath === action.id;
      // Copy the expanded map (immutability: never mutate state directly)
      let nextExpanded = { ...state.expanded };

      // If already expanded -> collapse (remove key)
      if (nextExpanded[action.parentMenuPath]) {
        nextExpanded = deleteKeysByPrefix(nextExpanded, action.parentMenuPath) as Record<
          string,
          true
        >;
      } else {
        // Otherwise expand (add- key)
        if (!isTopLevelMenu) {
          nextExpanded[action.parentMenuPath] = true;
        } else {
          // If it is a top-level menu, we only expand it and keep the rest of its sub-menus in default state
          nextExpanded[action.parentMenuPath] = true;
          const defaultExpandedKeys = convertToPathObject(action.activeMenuTree);
          nextExpanded = { ...nextExpanded, ...defaultExpandedKeys };
          Object.keys(nextExpanded).forEach((key) => {
            if (!key.startsWith(action.parentMenuPath)) {
              delete nextExpanded[key];
            }
          });
        }
      }

      // Return brand-new state object
      return {
        expanded: nextExpanded,
      };
    }

    case "COLLAPSE_ALL": {
      // Collapse everything (useful for a "Collapse all" button)
      return { expanded: {} };
    }

    default: {
      // Exhaustiveness safeguard (if you add new actions later)
      // TS should ensure we never reach here if all actions are handled.
      return state;
    }
  }
}

/**
 * 6) Indexing helpers
 * - We build a quick parent lookup so we can derive ancestor highlights.
 * - This is NOT UI state. It’s derived from the static menu tree.
 */
export type MenuIndex = {
  byId: Map<string, AppMenuSchemaType>;
  parentById: Map<string, string | null>;
};

/**
 * 6) Indexing helpers
 * - We build a quick parent lookup so we can derive ancestor highlights.
 * - This is NOT UI state. It’s derived from the static menu tree.
 */
export function buildMenuIndex(menus: AppMenuSchemaType[]): MenuIndex {
  const byId = new Map<string, AppMenuSchemaType>();
  const parentById = new Map<string, string | null>();

  const walk = (menus: AppMenuSchemaType[], parentId: string | null) => {
    for (const menu of menus) {
      byId.set(menu._id as string, menu);
      parentById.set(menu._id as string, parentId);

      // Only submenu has children, so we recurse only for type === "submenu"
      if (menu.action === MenuAction.OPEN_SUB_MENU && menu.children?.length) {
        walk(menu.children, menu._id as string);
      }
    }
  };

  walk(menus, null);
  return { byId, parentById };
}

/**
 * 7) Derivation helper: compute all ancestor submenu IDs for the active route ID
 * - This gives you "grey highlight" for submenus that contain the active route.
 * - IMPORTANT: We are *not* expanding these submenus automatically.
 *   We only compute them for styling.
 */
export function getAncestorSet(parentById: Map<string, string | null>, activeId?: string | null) {
  const set = new Set<string>();
  if (!activeId) return set;

  let cur = parentById.get(activeId) ?? null;
  while (cur) {
    set.add(cur);
    cur = parentById.get(cur) ?? null;
  }
  return set;
}

/**
 * 8) Context Value
 * - Provides: menuTree, index, UI state, dispatch
 * - Also provides derived styling helpers based on activeId from URL.
 */
export interface AppLiveContextProps {
  menuTree: AppMenuSchemaType[];
  homePageMenu?: AppMenuSchemaType;
  showLeftMenu: boolean;
  toggleLeftMenu: React.Dispatch<React.SetStateAction<boolean>>;
  onHamburgerClick: () => void;
  onNavbarButtonClick: () => void;
  onMenuClick: (params: MenuClickParams) => void;
  index: MenuIndex;

  state: MenuUIState;
  dispatch: React.Dispatch<UserMenuAction>;

  // Derived info for UI rendering (no storage):
  isExpanded: (id: string) => boolean;

  // Derived highlight helpers:
  isActiveRoute: (id: string) => boolean;
  isAncestorMenu: (id: string) => boolean;

  //Page Views
  isFormView: boolean;

  // App states
  appData?: {
    getApp: AppData;
  };
  isAppDataLoading: boolean;
  isPublishedAppBootstrapping: boolean;
  appError?: ErrorLike;
}

export type AppLiveContextForPublicFormProps = useQuery.Result<
  {
    getApp: AppData;
  },
  OperationVariables,
  "empty" | "complete" | "streaming",
  OperationVariables
>;

interface MenuClickParams {
  menu: AppMenuSchemaType;
  parentMenuPath: string;
  appId: string;
}

const AppLiveContext = createContext<AppLiveContextProps | undefined>(undefined);
const PublicFormAppContext = createContext<AppLiveContextForPublicFormProps | undefined>(undefined);

const PublishedAppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { appId, formId } = useParams();
  const [searchParams] = useSearchParams();
  const [showLeftMenu, toggleLeftMenu] = useState(false);
  const isMobile = !useIsMinimumTablet();
  const isFormView = useMemo(
    () => !!formId && pathname.startsWith(`/apps/${appId}/forms/${formId}`),
    [appId, formId, pathname],
  );

  const onHamburgerClick = useCallback(() => {
    toggleLeftMenu((prev) => !prev);
  }, [toggleLeftMenu]);

  const {
    data: appData,
    loading: isAppDataLoading,
    error: appError,
  } = useQuery<{
    getApp: AppData;
  }>(GET_APP, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables: { input: { appId: appId as string, isLiveMode: true } },
  });

  const isPublishedAppBootstrapping = isAppDataLoading || (!appData && !appError);

  const menuTree = useMemo(() => appData?.getApp?.navigation?.menus ?? [], [appData]);

  useEffect(() => {
    toggleLeftMenu(isMobile ? false : true);
  }, [isMobile]);

  // UI interaction state: only expanded submenus
  const [state, dispatch] = useReducer(menuReducer, {
    expanded: {},
  });

  const activeId = useMemo(() => searchParams.get("menuId"), [searchParams]);

  // Index the menu tree for fast lookups (id -> node, id -> parentId)
  const index = useMemo(() => buildMenuIndex(menuTree), [menuTree]);

  const homePageMenu = useMemo(
    () => index.byId.get(appData?.getApp?.navigation?.homePageMenuData?._id as string),
    [appData, index],
  );

  const onMenuClick = useCallback(
    ({ menu, appId, parentMenuPath }: MenuClickParams) => {
      if (isValidSubMenu(menu)) {
        const activeMenu = index.byId.get(activeId as string);
        dispatch({
          type: "TOGGLE_SUBMENU",
          id: menu._id as string,
          parentMenuPath,
          activeMenuTree: activeMenu?.parentTree ?? [],
        });
      } else {
        if (isMobile) {
          toggleLeftMenu(false);
        }
        const link = getLinkFromMenuParams(`/apps/${appId}`, menu);
        if (link.isExternalLink) {
          const didOpen = openExternalUrl({
            url: link.path,
            allowedProtocols: ["http:", "https:"],
          });
          if (!didOpen) {
            // eslint-disable-next-line no-console
            console.warn("Blocked unsafe external link", link.path);
          }
        } else {
          navigate(link.path);
        }
        dispatch({
          type: "COLLAPSE_ALL",
        });
      }
    },
    [activeId, index.byId, isMobile, navigate],
  );

  const onNavbarButtonClick = useCallback(() => {
    if (homePageMenu && appId) {
      const homePageMenuId = homePageMenu._id as string;
      onMenuClick({
        menu: homePageMenu,
        appId: appId as string,
        parentMenuPath: (homePageMenu.parentTree ?? []).join("/") + homePageMenuId,
      });
    } else {
      navigate("/apps");
    }
  }, [homePageMenu, onMenuClick, appId, navigate]);

  // Ancestor submenus of the active route for "grey highlight"
  const activeAncestors = useMemo(
    () => getAncestorSet(index.parentById, activeId),
    [index.parentById, activeId],
  );

  // isExpanded: checks reducer state (user-controlled only)
  const isExpanded = (id: string) => !!state.expanded[id];

  // isActiveRoute: true only when the ID matches activeId
  const isActiveRoute = useCallback((id: string) => !!activeId && id === activeId, [activeId]);

  // isAncestorMenu: true if submenu is an ancestor of active route
  const isAncestorMenu = useCallback((id: string) => activeAncestors.has(id), [activeAncestors]);

  const value: AppLiveContextProps = {
    menuTree,
    index,
    state,
    dispatch,
    isExpanded,
    isActiveRoute,
    isAncestorMenu,
    isAppDataLoading,
    isPublishedAppBootstrapping,
    appError,
    appData,
    homePageMenu,
    showLeftMenu,
    toggleLeftMenu,
    onHamburgerClick,
    isFormView,
    onMenuClick,
    onNavbarButtonClick,
  };

  return <AppLiveContext.Provider value={value}>{children}</AppLiveContext.Provider>;
};

const usePublishedApp = () => {
  const ctx = useContext(AppLiveContext);
  if (!ctx) throw new Error("usePublishedApp must be used within PublishedAppProvider");
  return ctx;
};

const PublicFormAppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { appId } = useParams();

  const appQueryDetails = useQuery<{
    getApp: AppData;
  }>(GET_APP, {
    fetchPolicy: "network-only",
    skip: !appId,
    variables: { input: { appId, isLiveMode: true } },
  });

  return (
    <PublicFormAppContext.Provider value={appQueryDetails}>
      {children}
    </PublicFormAppContext.Provider>
  );
};

const usePublishedAppForPublicForm = () => {
  const ctx = useContext(PublicFormAppContext);
  if (!ctx)
    throw new Error("usePublishedAppForPublicForm must be used within PublicFormAppProvider");
  return ctx;
};

export {
  PublishedAppProvider,
  usePublishedApp,
  usePublishedAppForPublicForm,
  PublicFormAppProvider,
};
